/* Product star rating shown above the product title.
   Extracted from global.js (see linked-variant-picker.js for rationale). */
if (!customElements.get('product-rating')) {
// Compact star rating shown above the product title (snippets/product-rating.liquid).
// Loads the product group's reviews from the same backend as assets/reviews.js,
// computes the average, and drives the theme's shared .rating-star gradient.
// Being a custom element means it re-populates itself for free after a linked
// variant section swap: the freshly-inserted element auto-upgrades and its
// connectedCallback runs again (served instantly from the per-group fetch cache).
class ProductRating extends HTMLElement {
    connectedCallback() {
        const group = this.dataset.productGroup;
        // Guard against connectedCallback firing more than once for the same node.
        if (!group || this.dataset.loaded === '1') return;
        this.dataset.loaded = '1';

        // Clicking the stars (or Enter/Space when focused) jumps to the reviews.
        // Bound now but only reachable once render() reveals + makes it focusable.
        this.addEventListener('click', ProductRating.scrollToReviews);
        this.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                ProductRating.scrollToReviews();
            }
        });

        ProductRating.fetchGroup(group)
            .then((reviews) => {
                // No reviews for this group -> leave the element hidden entirely.
                if (!Array.isArray(reviews) || reviews.length === 0) return;
                const avg = reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / reviews.length;
                this.render(avg, reviews.length);
            })
            .catch((err) => {
                console.error('product-rating: load failed', err);
            });
    }

    render(avg, count) {
        // Mirror the theme's native rating block: `--rating` is the whole-star
        // count and `--rating-decimal` buckets the fraction to 0 / 0.5 / 1, so the
        // shared .rating-star gradient renders half-star granularity identically.
        const full = Math.floor(avg);
        const dec = avg - full;
        let ratingDecimal = 0;
        if (dec >= 0.3 && dec <= 0.7) ratingDecimal = 0.5;
        else if (dec > 0.7) ratingDecimal = 1;

        const star = this.querySelector('.rating-star');
        if (star) {
            star.style.setProperty('--rating', full);
            star.style.setProperty('--rating-max', 5);
            star.style.setProperty('--rating-decimal', ratingDecimal);
        }

        const score = avg.toFixed(1).replace('.', ',');
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.setAttribute(
            'aria-label',
            `${score} von 5 Sternen bei ${count} ${count === 1 ? 'Bewertung' : 'Bewertungen'} – Bewertungen ansehen`
        );
        this.hidden = false;
    }

    static scrollToReviews() {
        const target = document.querySelector('.reviews-section');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // One in-flight/settled fetch per product group, shared across every
    // <product-rating> instance and across section swaps.
    static fetchGroup(group) {
        ProductRating._cache = ProductRating._cache || {};
        if (!ProductRating._cache[group]) {
            const url = `${ProductRating.API_BASE}/product-groups/${encodeURIComponent(group)}/reviews`;
            ProductRating._cache[group] = fetch(url, { headers: { Accept: 'application/json' } })
                .then((res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.json();
                });
        }
        return ProductRating._cache[group];
    }
}
// Keep in sync with API_BASE in assets/reviews.js.
ProductRating.API_BASE = 'https://chayas-reviews-staging.copetence.de';
customElements.define('product-rating', ProductRating);
}
